// App shell — prototype-faithful sidebar + topbar + content area.
// Ported from chitos-prototype/core.jsx (Sidebar + Topbar) using the
// prototype CSS classes now living in styles/prototype.css.

import { useEffect, useState, Fragment, type ReactElement, type ReactNode } from 'react';
import { NavLink, Link, useLocation } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth.js';
import { orpc } from '../lib/orpc.js';
import { current_due_month } from '../lib/format.js';
import { use_language, set_language } from '../i18n/index.js';
import { Icon } from './Icon.js';
import { ReadOnlyBanner } from './Entitlement.js';

const COLLAPSE_KEY = 'chitos.sidebar.collapsed';

interface NavItem {
  to: string;
  ta: string;
  en: string;
  icon: keyof typeof Icon;
  count?: number;
  /** Nested children — render in a sub-list under this entry. The parent's
   *  `to` link still works; children show with smaller font + indented icon.
   *  Auto-expanded whenever the URL matches the parent or any child. */
  sub_items?: NavItem[];
}

// Two-letter initials: "Suppu Senthil" → "SS".
function initials_from(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

// Notification kind → (tone class suffix, icon name) for the prototype's
// .notif-item tone-* styling.
const NOTIF_TONE: Record<string, { tone: string; icon: keyof typeof Icon }> = {
  due: { tone: 'warn', icon: 'cash' },
  overdue: { tone: 'warn', icon: 'bell' },
  payout: { tone: 'info', icon: 'cash' },
  payment: { tone: 'ok', icon: 'check' },
  kyc: { tone: 'info', icon: 'shield' },
  whatsapp: { tone: 'ok', icon: 'wa' },
  audit: { tone: 'info', icon: 'doc' },
  system: { tone: 'muted', icon: 'settings' },
};

interface NotifRow {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

export function Shell({ children }: { children: ReactNode }): ReactElement {
  const { logout, session } = useAuth();
  const lang = use_language();
  const loc = useLocation();
  const qc = useQueryClient();
  const has_platform_write = session?.scopes.includes('platform:write') ?? false;

  const [collapsed, set_collapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const [mobile_open, set_mobile_open] = useState(false);
  const [open_menu, set_open_menu] = useState<'notif' | 'profile' | null>(null);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close topbar dropdowns on outside click.
  useEffect(() => {
    const on_doc = (e: MouseEvent): void => {
      const el = e.target as HTMLElement;
      if (!el.closest('.topbar-menu') && !el.closest('.topbar-trigger')) {
        set_open_menu(null);
      }
    };
    document.addEventListener('mousedown', on_doc);
    return () => document.removeEventListener('mousedown', on_doc);
  }, []);

  // ---- Live nav count badges (cheap, cached) ----
  const chits_count_q = useQuery({
    queryKey: ['nav-count', 'chits'],
    queryFn: () => orpc.chits.list({ limit: 100 }),
    enabled: !has_platform_write,
    staleTime: 5 * 60 * 1000,
  });
  const dues_count_q = useQuery({
    queryKey: ['nav-count', 'dues', current_due_month()],
    queryFn: () =>
      orpc.dues.list({ due_month: current_due_month(), status: 'pending_or_overdue' }),
    enabled: !has_platform_write,
    staleTime: 5 * 60 * 1000,
  });
  const chits_count = (chits_count_q.data?.chits as unknown[] | undefined)?.length;
  const dues_count = (dues_count_q.data?.rows as unknown[] | undefined)?.length;

  // ---- Notifications ----
  const notif_q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => orpc.notifications.list({}),
    refetchInterval: 30_000,
  });
  const mark_all = useMutation({
    mutationFn: () => orpc.notifications.mark_all_read({}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const mark_one = useMutation({
    mutationFn: (id: string) => orpc.notifications.mark_read({ notification_id: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const notifs = (notif_q.data?.notifications ?? []) as NotifRow[];
  const unread = notif_q.data?.unread_count ?? 0;

  // ---- Profile ----
  const profile_q = useQuery({
    queryKey: ['me'],
    queryFn: () => orpc.me.get({}),
    enabled: Boolean(session) && session?.kind !== 'subscriber',
    staleTime: 5 * 60 * 1000,
  });
  const profile = profile_q.data?.profile;
  const display_name = profile?.name ?? 'ChitDesk';
  const tenant_name = profile?.tenant_name ?? 'ChitDesk';
  const ava = profile ? initials_from(profile.name) : 'CO';

  // ---- Nav sections ----
  const operations: NavItem[] = [
    { to: '/dashboard', ta: 'முகப்பு', en: 'Dashboard', icon: 'home' },
    { to: '/dues', ta: 'நிலுவைகள்', en: 'Dues', icon: 'cash', count: dues_count },
    { to: '/chits', ta: 'சீட்டுகள்', en: 'Chits', icon: 'chits', count: chits_count },
    { to: '/subscribers', ta: 'உறுப்பினர்கள்', en: 'Members', icon: 'people' },
    { to: '/transactions', ta: 'பரிவர்த்தனைகள்', en: 'Transactions', icon: 'receipt' },
  ];
  const insights: NavItem[] = [
    {
      to: '/reports',
      ta: 'அறிக்கைகள்',
      en: 'Reports',
      icon: 'reports',
      sub_items: [
        { to: '/reports/collection-day', ta: 'வசூல் நாள் பட்டியல்', en: 'Collection Day', icon: 'people' },
        { to: '/reports/aging', ta: 'வயது அறிக்கை', en: 'Aging', icon: 'bell' },
        { to: '/reports/day-book', ta: 'தினசரி கணக்கு', en: 'Day Book', icon: 'cash' },
        { to: '/reports/foreman-commission', ta: 'ஃபோர்மேன் கமிஷன்', en: 'Foreman Commission', icon: 'reports' },
        { to: '/reports/member-statement', ta: 'உறுப்பினர் அறிக்கை', en: 'Member Statement', icon: 'doc' },
        { to: '/reports/chit-pnl', ta: 'சீட்டு லாபம்/நஷ்டம்', en: 'Chit P&L', icon: 'chits' },
        { to: '/reports/payout-forecast', ta: 'பணம் வழங்கல் கணிப்பு', en: 'Payout Forecast', icon: 'gavel' },
        { to: '/reports/comms', ta: 'தொடர்பு பகுப்பாய்வு', en: 'Comms Analytics', icon: 'wa' },
      ],
    },
  ];
  const platform: NavItem[] = has_platform_write
    ? [
        { to: '/whatsapp', ta: 'வாட்ஸ்அப்', en: 'WhatsApp', icon: 'wa' },
        { to: '/calls', ta: 'அழைப்புகள்', en: 'Calls', icon: 'phone' },
        { to: '/admin/tenants', ta: 'குத்தகைதாரர்கள்', en: 'Tenants', icon: 'people' },
        { to: '/admin/plans', ta: 'திட்டங்கள்', en: 'Plans', icon: 'doc' },
        { to: '/admin/transactions', ta: 'பரிவர்த்தனைகள்', en: 'Transactions', icon: 'cash' },
        { to: '/admin/flags', ta: 'அம்சக் கொடிகள்', en: 'Feature flags', icon: 'settings' },
        { to: '/admin/analytics', ta: 'பகுப்பாய்வு', en: 'Analytics', icon: 'reports' },
      ]
    : [
        { to: '/whatsapp', ta: 'வாட்ஸ்அப்', en: 'WhatsApp', icon: 'wa' },
        { to: '/calls', ta: 'அழைப்புகள்', en: 'Calls', icon: 'phone' },
      ];

  // ---- Breadcrumb + back button, derived from the nav definitions (DRY) ----
  const nav_flat = [...operations, ...insights, ...platform].flatMap((it) => [
    { to: it.to, ta: it.ta, en: it.en },
    ...(it.sub_items ?? []).map((s) => ({ to: s.to, ta: s.ta, en: s.en })),
  ]);
  const seg_list = loc.pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; to?: string }> = [
    { label: lang === 'ta' ? 'பணிமேடை' : 'Workspace', to: '/dashboard' },
  ];
  for (let i = 0, cum = ''; i < seg_list.length; i++) {
    cum += '/' + seg_list[i];
    const hit = nav_flat.find((n) => n.to === cum);
    if (!hit) continue; // skip dynamic ids / non-routable segments
    crumbs.push({ label: lang === 'ta' ? hit.ta : hit.en, to: cum === loc.pathname ? undefined : cum });
  }

  const nav_label = (it: NavItem): string => (lang === 'ta' ? it.ta : it.en);

  const Section = ({ items, label }: { items: NavItem[]; label: string }): ReactElement => (
    <div className="nav-section">
      <div className="nav-label">{label}</div>
      {items.map((it) => {
        const I = Icon[it.icon];
        // Expand the sub-list whenever the current route lives under this
        // parent. Operators land on /reports/aging from a bookmark and see
        // the whole reports group already opened, not collapsed.
        const has_subs = (it.sub_items?.length ?? 0) > 0;
        const child_active = has_subs && it.sub_items!.some((s) => loc.pathname.startsWith(s.to));
        // Parent is "active" when its exact URL is matched (NavLink end-style)
        // OR a child is active — gives the operator a clear "you're in this
        // group" affordance.
        const parent_active = loc.pathname === it.to || child_active;
        return (
          <div key={it.to}>
            <NavLink
              to={it.to}
              onClick={() => mobile_open && set_mobile_open(false)}
              title={collapsed ? nav_label(it) : undefined}
              end={has_subs}
              className={'nav-item' + (parent_active ? ' active' : '')}
            >
              <span className="nav-icon">
                <I />
              </span>
              <span className={'nav-text ' + (lang === 'ta' ? 'tamil' : '')}>{nav_label(it)}</span>
              {it.count !== undefined && it.count > 0 && (
                <span className="nav-count mono">{it.count}</span>
              )}
              {it.count !== undefined && it.count > 0 && <span className="nav-count-dot" />}
            </NavLink>
            {has_subs && !collapsed && parent_active && (
              <div className="nav-sub">
                {it.sub_items!.map((sub) => {
                  const SI = Icon[sub.icon];
                  return (
                    <NavLink
                      key={sub.to}
                      to={sub.to}
                      onClick={() => mobile_open && set_mobile_open(false)}
                      className={({ isActive }) => 'nav-item nav-item-sub' + (isActive ? ' active' : '')}
                    >
                      <span className="nav-icon">
                        <SI />
                      </span>
                      <span className={'nav-text ' + (lang === 'ta' ? 'tamil' : '')}>
                        {nav_label(sub)}
                      </span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={'app' + (collapsed ? ' sidebar-collapsed' : '')}>
      {/* ===== Sidebar ===== */}
      {mobile_open && <div className="sidebar-scrim" onClick={() => set_mobile_open(false)} />}
      <aside className={'sidebar' + (collapsed ? ' collapsed' : '') + (mobile_open ? ' mobile-open' : '')}>
        <div className="brand">
          <div className="brand-mark" title={tenant_name}>
            {initials_from(tenant_name).slice(0, 1)}
          </div>
          <div className="brand-text">
            <div className="brand-name">{tenant_name}</div>
            <div className="brand-sub mono">ChitDesk</div>
          </div>
        </div>

        <Section items={operations} label={lang === 'ta' ? 'செயல்பாடுகள்' : 'Operations'} />
        <Section items={insights} label={lang === 'ta' ? 'சிறப்பு' : 'Insights'} />
        <Section items={platform} label={lang === 'ta' ? 'தளம்' : 'Platform'} />

        <div className="tenant-foot">
          <div className="tenant-foot-mark">{ava}</div>
          <div className="tenant-foot-text" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, color: 'var(--ink)', fontSize: 12 }}>{display_name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {lang === 'ta' ? 'நிர்வாகி' : profile?.role ?? 'Foreman'}
            </div>
          </div>
        </div>
      </aside>
      <button
        className="sidebar-toggle"
        onClick={() => set_collapsed((v) => !v)}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? <Icon.chevR /> : <Icon.chevL />}
      </button>

      {/* ===== Main ===== */}
      <div className="main">
        <div className="topbar">
          <div className="crumbs">
            {crumbs.map((c, i) => (
              <Fragment key={i}>
                {i > 0 && <span>›</span>}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <strong>{c.label}</strong>}
              </Fragment>
            ))}
          </div>
          <div className="searchbox">
            <Icon.search />
            <input placeholder={lang === 'ta' ? 'உறுப்பினர், சீட்டு, பரிவர்த்தனை...' : 'Member, chit, transaction…'} />
            <span className="kbd">⌘K</span>
          </div>
          <div className="lang-toggle">
            <button className={lang === 'ta' ? 'on' : ''} onClick={() => set_language('ta')}>
              தமிழ்
            </button>
            <button className={lang === 'en' ? 'on' : ''} onClick={() => set_language('en')}>
              EN
            </button>
          </div>

          {/* Help */}
          <Link
            to="/help"
            className={'icon-btn' + (loc.pathname === '/help' ? ' active' : '')}
            title={lang === 'ta' ? 'உதவி & வழிகாட்டி' : 'Help & guide'}
          >
            <Icon.help />
          </Link>

          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button
              className={'icon-btn topbar-trigger' + (open_menu === 'notif' ? ' active' : '')}
              title={lang === 'ta' ? 'அறிவிப்புகள்' : 'Notifications'}
              onClick={() => set_open_menu(open_menu === 'notif' ? null : 'notif')}
            >
              <Icon.bell />
              {unread > 0 && <span className="notif-dot">{unread > 99 ? '99+' : unread}</span>}
            </button>
            {open_menu === 'notif' && (
              <div className="topbar-menu notif-menu">
                <div className="tm-head">
                  <div>
                    <div className="tm-title">{lang === 'ta' ? 'அறிவிப்புகள்' : 'Notifications'}</div>
                    <div className="tm-sub">
                      {unread} {lang === 'ta' ? 'படிக்கப்படாதவை' : 'unread'}
                    </div>
                  </div>
                  {unread > 0 && (
                    <button className="tm-link" onClick={() => mark_all.mutate()} disabled={mark_all.isPending}>
                      {lang === 'ta' ? 'எல்லாம் வாசிக்கப்பட்டது' : 'Mark all read'}
                    </button>
                  )}
                </div>
                <div className="tm-list">
                  {notifs.map((n) => {
                    const meta = NOTIF_TONE[n.kind] ?? { tone: 'info', icon: 'bell' as const };
                    const I = Icon[meta.icon];
                    return (
                      <div
                        key={n.id}
                        className={'notif-item ' + (n.is_read ? '' : 'unread ') + 'tone-' + meta.tone}
                        onClick={() => !n.is_read && mark_one.mutate(n.id)}
                      >
                        <div className="notif-ico">
                          <I />
                        </div>
                        <div className="notif-body">
                          <div className={'notif-title ' + (lang === 'ta' ? 'tamil' : '')}>{n.title}</div>
                          {n.body && <div className={'notif-text ' + (lang === 'ta' ? 'tamil' : '')}>{n.body}</div>}
                          <div className="notif-time">
                            {new Date(n.created_at).toLocaleString(lang === 'ta' ? 'ta-IN' : 'en-IN')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {notifs.length === 0 && (
                    <div className="notif-empty">{lang === 'ta' ? 'எதுவும் இல்லை' : 'All caught up'}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User pill */}
          <button
            className={'user-pill topbar-trigger' + (open_menu === 'profile' ? ' active' : '')}
            onClick={() => set_open_menu(open_menu === 'profile' ? null : 'profile')}
          >
            <div className="ava">{ava}</div>
            <span>{display_name.split(/\s+/)[0]}</span>
            <svg className="caret" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M2 4l3 3 3-3" />
            </svg>
          </button>
          {open_menu === 'profile' && (
            <div className="topbar-menu profile-menu">
              <div className="pm-head">
                <div className="pm-ava">{ava}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="pm-name">{display_name}</div>
                  <div className="pm-role">{profile?.role ?? '—'}</div>
                  <div className="pm-meta">{profile?.email ?? ''}</div>
                </div>
              </div>
              <div className="pm-sec">
                <Link to="/settings" className="pm-item" onClick={() => set_open_menu(null)}>
                  <Icon.settings />
                  <span>{lang === 'ta' ? 'அமைப்புகள்' : 'Settings'}</span>
                </Link>
                <Link to="/profile" className="pm-item" onClick={() => set_open_menu(null)}>
                  <Icon.user />
                  <span>{lang === 'ta' ? 'என் சுயவிவரம்' : 'My profile'}</span>
                </Link>
                <Link to="/profile" className="pm-item" onClick={() => set_open_menu(null)}>
                  <Icon.key />
                  <span>{lang === 'ta' ? 'கடவுச்சொல் மாற்று' : 'Change password'}</span>
                </Link>
              </div>
              <div className="pm-sec danger">
                <button className="pm-item danger" onClick={() => void logout()}>
                  <Icon.logout />
                  <span>{lang === 'ta' ? 'வெளியேறு' : 'Sign out'}</span>
                </button>
              </div>
              <div className="pm-foot">
                <span>ChitDesk</span>
                <span>·</span>
                <span>{profile?.email ?? ''}</span>
              </div>
            </div>
          )}
        </div>

        <ReadOnlyBanner />
        {children}
      </div>
    </div>
  );
}
